import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Row, Col, Avatar, Typography, Space, Divider, message, Upload } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, CameraOutlined, LoadingOutlined } from '@ant-design/icons';
import * as adminactions from 'store/actions/AuthActions';
import axios from 'axios';

const { Title, Text } = Typography;

const EmployeeProfile = ({ employeeId }) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);

  useEffect(() => {
    if (employeeId) {
      loadEmployeeData();
    }
  }, [employeeId]);

  const loadEmployeeData = async () => {
    try {
      setLoading(true);
      const response = await adminactions.retrieveEmployee(employeeId);
      if (response && !response.message) {
        setEmployee(response);
        const user = response.user;
        if (user) {
          form.setFieldsValue({
            first_name: user.first_name,
            last_name: user.last_name,
            username: user.username,
            email: user.email,
            phone: user.phone || '',
            personal_email: response.personal_email || '',
            date_joined: user.date_joined ? new Date(user.date_joined).toLocaleDateString() : '',
          });
          // Set profile picture URL
          setProfilePictureUrl(user.profile_picture_url || null);
        }
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      message.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      if (employee?.user?.id) {
        // Update user information
        const userValues = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone,
        };
        const response = await adminactions.updateUsers(userValues, employee.user.id);

        // Update employee personal_email
        if (values.personal_email !== undefined) {
          await axios.patch(`/employee/employees/${employeeId}/`, {
            personal_email: values.personal_email || null
          });
        }

        if (response && !response.message) {
          message.success('Profile updated successfully!');
          setIsEditing(false);
          loadEmployeeData();
        } else {
          message.error(response?.message || 'Failed to update profile');
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('An error occurred while updating profile');
    }
  };

  const handleProfilePictureUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await axios.patch(`/user/user/${employee.user.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data) {
        message.success('Profile photo updated successfully!');
        setProfilePictureUrl(response.data.profile_picture_url);
        loadEmployeeData();
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      message.error('Failed to upload profile photo');
    } finally {
      setUploadingPhoto(false);
    }
    return false; // Prevent default upload behavior
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.resetFields();
    // Reload form with original data
    if (employee?.user) {
      form.setFieldsValue({
        first_name: employee.user.first_name,
        last_name: employee.user.last_name,
        username: employee.user.username,
        email: employee.user.email,
        phone: employee.user.phone || '',
        personal_email: employee.personal_email || '',
        date_joined: employee.user.date_joined ? new Date(employee.user.date_joined).toLocaleDateString() : '',
      });
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!employee || !employee.user) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>No user data available</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Upload
                name="profile_picture"
                showUploadList={false}
                beforeUpload={handleProfilePictureUpload}
                accept="image/*"
              >
                <div style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}>
                  <Avatar
                    size={120}
                    src={profilePictureUrl}
                    icon={uploadingPhoto ? <LoadingOutlined /> : <UserOutlined />}
                    style={{ marginBottom: '16px' }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '16px',
                      right: '0',
                      background: '#1890ff',
                      borderRadius: '50%',
                      padding: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <CameraOutlined style={{ color: 'white', fontSize: '14px' }} />
                  </div>
                </div>
              </Upload>
              <Title level={4}>{employee.user.first_name} {employee.user.last_name}</Title>
              <Text type="secondary">{employee.user.username}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>Click photo to change</Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={16} md={18}>
          <Card title="Personal Information"
                extra={
                  !isEditing ? (
                    <Button type="primary" onClick={handleEdit}>
                      Edit
                    </Button>
                  ) : null
                }>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              disabled={!isEditing}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="First Name"
                    name="first_name"
                    rules={[{ required: true, message: 'Please input first name!' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Last Name"
                    name="last_name"
                    rules={[{ required: true, message: 'Please input last name!' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Last Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Username"
                    name="username"
                  >
                    <Input prefix={<UserOutlined />} placeholder="Username" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Please input email!' },
                      { type: 'email', message: 'Please enter a valid email!' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Email" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Phone"
                    name="phone"
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Date Joined"
                    name="date_joined"
                  >
                    <Input prefix={<CalendarOutlined />} placeholder="Date Joined" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Personal Email ID"
                    name="personal_email"
                    rules={[
                      { type: 'email', message: 'Please enter a valid email!' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Personal Email" />
                  </Form.Item>
                </Col>
              </Row>

              {isEditing && (
                <>
                  <Divider />
                  <Space>
                    <Button type="primary" htmlType="submit">
                      Save Changes
                    </Button>
                    <Button onClick={handleCancel}>
                      Cancel
                    </Button>
                  </Space>
                </>
              )}
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EmployeeProfile;
